import React, { useState, useEffect, useContext } from 'react';
import ApiURL from '../Api/Api';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SidebarContext } from './SidebarContext';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import {
  BuildingOfficeIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  Squares2X2Icon,
  TableCellsIcon,
  XMarkIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

/* ─────────────────────────────────────────────
   SITE ROW CARD — light theme
───────────────────────────────────────────── */
const SiteRowCard = ({ site, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.04, duration: 0.25 }}
    className="bg-white rounded-xl border border-slate-200 px-4 py-3.5 hover:border-indigo-300 hover:shadow-sm transition-all duration-200 group"
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        {/* Index badge */}
        <div className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0">
          <span className="text-[10px] font-extrabold text-slate-400">{String(index + 1).padStart(2, '0')}</span>
        </div>
        <div>
          <p className="text-[13px] font-bold text-slate-700 group-hover:text-indigo-600 transition-colors leading-tight">
            {site.site_name || '—'}
          </p>
          <p className="text-[10px] text-slate-400 font-medium mt-0.5">by {site.created_by || '—'}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-[11px] font-semibold text-slate-500">{site.created_date || '—'}</p>
        <div className="flex items-center justify-end gap-1 mt-1">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          <span className="text-[9px] font-extrabold text-emerald-600 uppercase tracking-wider">Active</span>
        </div>
      </div>
    </div>
  </motion.div>
);

/* ═══════════════════════════════════════════════════════════════════════ */
const SiteManager = () => {
  const userName = localStorage.getItem('user_name') || 'Unknown';
  const companyId = localStorage.getItem('company_id') || '';
  const { isSidebarOpen } = useContext(SidebarContext);

  const newSiteTemplate = () => ({
    company_id: companyId,
    created_by: userName,
    created_date: new Date().toISOString().split('T')[0],
    site_name: '',
  });

  const [sites, setSites] = useState([newSiteTemplate()]);
  const [siteList, setSiteList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) return 'cards';
    return 'table';
  });
  const navigate = useNavigate();

  useEffect(() => { fetchSites(); }, []);

  /* ── Detect mobile → default card view ── */
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768 && viewMode === 'table') {
        setViewMode('cards');
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [viewMode]);

  const fetchSites = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${ApiURL}/getSites.php?company_id=${companyId}`);
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      if (data?.status === 'success' && Array.isArray(data.sites)) {
        setSiteList(data.sites);
      } else {
        throw new Error(data?.message || 'Failed to fetch sites');
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSite = () => {
    setSites(prev => [...prev, newSiteTemplate()]);
  };

  const handleRemoveSite = (index) => {
    if (sites.length === 1) return;
    setSites(prev => prev.filter((_, i) => i !== index));
  };

  const handleInputChange = (index, field, value) => {
    setSites(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const invalid = sites.some(s => !s.site_name.trim() || !s.created_date);
    if (invalid) { toast.error('Please fill all required fields'); return; }
    setIsSubmitting(true);
    try {
      const response = await fetch(`${ApiURL}/addSite.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sites }),
      });
      const data = await response.json();
      if (response.ok && data.status === 'success') {
        toast.success(`${sites.length} site${sites.length > 1 ? 's' : ''} added successfully`);
        setSites([newSiteTemplate()]);
        fetchSites();
      } else {
        throw new Error(data.message || 'Failed to add site');
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredSites = searchQ.trim()
    ? siteList.filter(s =>
        (s.site_name || '').toLowerCase().includes(searchQ.toLowerCase()) ||
        (s.created_by || '').toLowerCase().includes(searchQ.toLowerCase())
      )
    : siteList;

  return (
    <div className="min-h-screen bg-[#f5f6fa]">
      <Navbar />
      <div className="flex pt-16">
        <Sidebar />
        <main className={`flex-1 transition-all duration-300 w-full ${isSidebarOpen ? 'lg:ml-72' : ''}`}>
          <div className="w-full max-w-5xl mx-auto px-3 py-4 sm:px-5 sm:py-6 pb-16">

            {/* ── PAGE HEADER ── */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="bg-white rounded-2xl border border-slate-200 mb-5 shadow-sm overflow-hidden"
            >
              <div className="h-[2px] bg-gradient-to-r from-indigo-500 to-emerald-500" />
              <div className="px-5 py-5 sm:px-6 sm:py-6 flex items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                    <p className="text-[9px] font-extrabold uppercase tracking-[0.25em] text-indigo-600">Site Management</p>
                  </div>
                  <h1 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight leading-none">Add Sites</h1>
                  <p className="text-[11px] text-slate-400 mt-1.5 font-medium">
                    Register new project sites for your company.
                  </p>
                </div>
                {/* Stats pill */}
                <div className="flex-shrink-0 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-center">
                  <p className="text-[9px] font-extrabold text-slate-400 uppercase tracking-wider mb-0.5">Total Sites</p>
                  <p className="text-2xl font-black text-slate-800 leading-none">{siteList.length}</p>
                </div>
              </div>
            </motion.div>

            {/* ── ADD SITE FORM ── */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08, duration: 0.32 }}
              className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-5 shadow-sm"
            >
              <div className="h-[2px] bg-gradient-to-r from-indigo-500 to-emerald-500" />
              <div className="px-4 pt-4 pb-5 sm:px-6 sm:pt-5">

                {/* Form header */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-[9px] font-extrabold text-slate-400 uppercase tracking-[0.18em] mb-0.5">Form</p>
                    <h2 className="text-[13px] font-black text-slate-700">Site Details</h2>
                  </div>
                  <span className="text-[10px] font-bold text-slate-500 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-lg">
                    {sites.length} {sites.length === 1 ? 'entry' : 'entries'}
                  </span>
                </div>

                <form onSubmit={handleSubmit}>
                  {/* Site rows */}
                  <div className="space-y-3 mb-4">
                    <AnimatePresence>
                      {sites.map((site, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, height: 0, y: -8 }}
                          animate={{ opacity: 1, height: 'auto', y: 0 }}
                          exit={{ opacity: 0, height: 0, y: -4 }}
                          transition={{ duration: 0.22 }}
                          className="overflow-hidden"
                        >
                          <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 hover:border-indigo-200 transition-colors">
                            <div className="flex items-start gap-3">
                              {/* Row number */}
                              <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0 mt-5">
                                <span className="text-[10px] font-extrabold text-slate-400">{String(index + 1).padStart(2, '0')}</span>
                              </div>

                              {/* Fields */}
                              <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {/* Site Name */}
                                <div>
                                  <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-[0.18em] block mb-1.5">
                                    Site Name <span className="text-rose-400">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={site.site_name}
                                    onChange={e => handleInputChange(index, 'site_name', e.target.value)}
                                    placeholder="e.g. Site Alpha"
                                    required
                                    className="w-full h-9 px-3 text-[12px] font-medium text-slate-700 placeholder-slate-300 bg-white border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
                                  />
                                </div>

                                {/* Created By */}
                                <div>
                                  <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-[0.18em] block mb-1.5">
                                    Created By
                                  </label>
                                  <input
                                    type="text"
                                    value={site.created_by}
                                    disabled
                                    className="w-full h-9 px-3 text-[12px] font-medium text-slate-400 bg-slate-100 border border-slate-200 rounded-lg cursor-not-allowed select-none"
                                  />
                                </div>

                                {/* Created Date */}
                                <div>
                                  <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-[0.18em] block mb-1.5">
                                    Date <span className="text-rose-400">*</span>
                                  </label>
                                  <input
                                    type="date"
                                    value={site.created_date}
                                    onChange={e => handleInputChange(index, 'created_date', e.target.value)}
                                    required
                                    className="w-full h-9 px-3 text-[12px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
                                  />
                                </div>
                              </div>

                              {/* Remove button */}
                              {sites.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveSite(index)}
                                  className="mt-5 w-8 h-8 rounded-lg bg-rose-50 border border-rose-200 text-rose-500 flex items-center justify-center hover:bg-rose-100 hover:border-rose-300 transition-all flex-shrink-0 touch-manipulation"
                                  aria-label="Remove site"
                                >
                                  <XMarkIcon className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={handleAddSite}
                      className="flex items-center gap-2 h-9 px-4 bg-white border border-slate-200 text-slate-600 hover:text-indigo-600 hover:border-indigo-300 text-[11px] font-bold rounded-lg transition-all touch-manipulation"
                    >
                      <PlusIcon className="w-3.5 h-3.5" />
                      Add Another
                    </button>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex items-center gap-2 h-9 px-5 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white text-[11px] font-extrabold rounded-lg transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ml-auto"
                    >
                      {isSubmitting ? (
                        <>
                          <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                          Saving…
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon className="w-3.5 h-3.5" />
                          Save {sites.length > 1 ? `${sites.length} Sites` : 'Site'}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>

            {/* ── SITE LIST ── */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.32 }}
              className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm"
            >
              <div className="h-[2px] bg-gradient-to-r from-slate-300 via-slate-400 to-slate-500" />
              <div className="px-4 pt-4 pb-5 sm:px-6 sm:pt-5">

                {/* List header */}
                <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                  <div>
                    <p className="text-[9px] font-extrabold text-slate-400 uppercase tracking-[0.18em] mb-0.5">Registry</p>
                    <h2 className="text-[13px] font-black text-slate-700">All Sites</h2>
                  </div>

                  <div className="flex items-center gap-2 flex-1 sm:flex-initial justify-end">
                    {/* Search */}
                    <div className="relative flex-1 sm:w-52 sm:flex-initial">
                      <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                      <input
                        type="text"
                        value={searchQ}
                        onChange={e => setSearchQ(e.target.value)}
                        placeholder="Search sites…"
                        className="w-full h-8 pl-8 pr-3 text-[11px] font-medium text-slate-700 placeholder-slate-400 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
                      />
                      {searchQ && (
                        <button onClick={() => setSearchQ('')} className="absolute right-2.5 top-1/2 -translate-y-1/2">
                          <XMarkIcon className="w-3 h-3 text-slate-400 hover:text-slate-600" />
                        </button>
                      )}
                    </div>

                    {/* View toggle */}
                    <div className="flex items-center bg-slate-100 border border-slate-200 rounded-lg p-0.5 gap-0.5 flex-shrink-0">
                      <button
                        onClick={() => setViewMode('cards')}
                        className={`w-7 h-7 rounded-md flex items-center justify-center transition-all duration-200 touch-manipulation ${
                          viewMode === 'cards'
                            ? 'bg-indigo-600 text-white shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                        aria-label="Card view"
                      >
                        <Squares2X2Icon className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setViewMode('table')}
                        className={`w-7 h-7 rounded-md flex items-center justify-center transition-all duration-200 touch-manipulation ${
                          viewMode === 'table'
                            ? 'bg-indigo-600 text-white shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                        aria-label="Table view"
                      >
                        <TableCellsIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Refresh */}
                    <button
                      onClick={fetchSites}
                      disabled={isLoading}
                      className="w-8 h-8 flex items-center justify-center bg-white border border-slate-200 text-slate-500 hover:text-indigo-600 hover:border-indigo-300 rounded-lg transition-all touch-manipulation disabled:opacity-40"
                      aria-label="Refresh"
                    >
                      <ArrowPathIcon className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </div>

                {/* Result count */}
                {searchQ && (
                  <div className="mb-3 flex items-center gap-1.5">
                    <span className="text-[10px] text-slate-500 font-medium">
                      <span className="text-indigo-600 font-bold">{filteredSites.length}</span> of {siteList.length} results
                    </span>
                  </div>
                )}

                {/* ── LOADING SKELETON ── */}
                {isLoading && (
                  <div className="space-y-2">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="bg-slate-50 rounded-xl border border-slate-200 h-16 overflow-hidden">
                        <div className="h-full p-4 flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-slate-200 animate-pulse flex-shrink-0" />
                          <div className="flex-1 space-y-1.5">
                            <div className="h-2.5 w-32 bg-slate-200 rounded animate-pulse" />
                            <div className="h-2 w-20 bg-slate-200 rounded animate-pulse" />
                          </div>
                          <div className="space-y-1.5 text-right">
                            <div className="h-2.5 w-20 bg-slate-200 rounded animate-pulse" />
                            <div className="h-2 w-12 bg-slate-200 rounded animate-pulse ml-auto" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ── CARD VIEW ── */}
                {!isLoading && viewMode === 'cards' && (
                  <div className="space-y-2">
                    {filteredSites.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center">
                          <BuildingOfficeIcon className="w-6 h-6 text-slate-300" />
                        </div>
                        <p className="text-[12px] text-slate-400 font-medium">
                          {searchQ ? 'No sites match your search' : 'No sites added yet'}
                        </p>
                      </div>
                    ) : (
                      filteredSites.map((site, i) => <SiteRowCard key={site.site_id || i} site={site} index={i} />)
                    )}
                  </div>
                )}

                {/* ── TABLE VIEW (responsive with horizontal scroll) ── */}
                {!isLoading && viewMode === 'table' && (
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <div className="min-w-[500px]">
                      <table className="w-full text-left text-[11px]">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            {['#', 'Site Name', 'Created Date', 'Created By', 'Status'].map(h => (
                              <th key={h} className="px-4 py-3 text-[9px] font-extrabold text-slate-500 uppercase tracking-[0.18em] whitespace-nowrap">
                                {h}
                              </th>
                            ))}
                           </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {filteredSites.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="px-4 py-10 text-center">
                                <div className="flex flex-col items-center gap-2">
                                  <BuildingOfficeIcon className="w-8 h-8 text-slate-300" />
                                  <span className="text-[11px] text-slate-400 font-medium">
                                    {searchQ ? 'No matching sites' : 'No sites found'}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            filteredSites.map((site, index) => (
                              <motion.tr
                                key={site.site_id || index}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: index * 0.03 }}
                                className="hover:bg-slate-50/80 transition-colors group"
                              >
                                <td className="px-4 py-3">
                                  <span className="text-[10px] font-extrabold text-slate-400">
                                    {String(index + 1).padStart(2, '0')}
                                  </span>
                                </td>
                                <td className="px-4 py-3 font-bold text-slate-700 group-hover:text-indigo-600 transition-colors whitespace-nowrap">
                                  {site.site_name || '—'}
                                </td>
                                <td className="px-4 py-3 text-slate-500 font-medium whitespace-nowrap">
                                  {site.created_date || '—'}
                                </td>
                                <td className="px-4 py-3 text-slate-500 font-medium whitespace-nowrap">
                                  {site.created_by || '—'}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-1.5">
                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                    <span className="text-[9px] font-extrabold text-emerald-600 uppercase tracking-wider">Active</span>
                                  </div>
                                </td>
                              </motion.tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Footer count */}
                {!isLoading && filteredSites.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-[10px] text-slate-400 font-medium">
                      Showing <span className="text-slate-600 font-bold">{filteredSites.length}</span> site{filteredSites.length !== 1 ? 's' : ''}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      <span className="text-[9px] font-extrabold text-emerald-600 uppercase tracking-wider">All Active</span>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Mobile bottom spacer */}
            <div className="h-8 sm:hidden" />
          </div>
        </main>
      </div>
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
    </div>
  );
};

export default SiteManager;